import * as React from "react";
import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import TelemetryIcon from "@mui/icons-material/Insights";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import SummarizeIcon from "@mui/icons-material/Summarize";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import AssessmentIcon from "@mui/icons-material/Assessment";
import Summary from "./Summary";
import Logs from "./Logs";
import Command from "./Command";
import Reports from "./Reports";
import ConsumptionSettings from "./ConsumptionSettings";
import ElectricMeterIcon from "@mui/icons-material/ElectricMeter";
import { getCurrentTenant } from "../../config/tenantConfig";
import LogsIcon from "@mui/icons-material/History";
import CommandIcon from "@mui/icons-material/Terminal";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import TelemetryHistory from "./TelemetryHistory";
import AlarmHistory from "./AlarmHistory";
import { blue, green, deepPurple, orange , cyan, brown} from "@mui/material/colors";
import Configuration from "./Configuration";
import SettingsIcon from "@mui/icons-material/Settings";
import IntervalSelector from "../../components/IntervalSelector";
import { useRefreshInterval } from "../../contexts/RefreshIntervalContext";
import { getNodeById, isNodeOnline } from "../../api/nodeAPI";
import { usePageVisibility } from "../../hooks/usePageVisibility";
import { getPlaceIdFromPathname } from "../../utils/pathUtils";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  if (value !== index) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      <Box sx={{ p: 3 }}>{children}</Box>
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function DetailHeader() {
  const { t } = useTranslation();
  const tenant = getCurrentTenant();
  const isSarj = tenant.localeOverride === "SARJ";
  // RCU: hide the Logs and Reports detail tabs.
  const isRcu = tenant.projectCode === "RCU";
  const [value, setValue] = React.useState(0);
  const [nodeData, setNodeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nodeStatus, setNodeStatus] = useState("offline");
  const [countdown, setCountdown] = useState(0);
  const { idNode } = useParams();
  const location = useLocation();
  const { interval, isRunning } = useRefreshInterval();

  const isPageVisibility = usePageVisibility();
  const node_idNode = getPlaceIdFromPathname(location.pathname);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    const fetchNodeData = async () => {
      try {
        setLoading(true);
        const data = await getNodeById(idNode);

        if (data) {
          setNodeData(data);
        } else {
          setError("No data available or error in response");
        }
      } catch (err) {
        setError(err.message || "An error occurred while fetching data");
      } finally {
        setLoading(false);
      }
    };

    if (idNode) {
      fetchNodeData();
    }
  }, [idNode]);

  useEffect(() => {
    let intervalId;
    let countdownIntervalId;
    const fetchDevices = async () => {
      try {
        const isOnline = await isNodeOnline(node_idNode);

        if (isOnline === true) {
          setNodeStatus("online");
        } else {
          setNodeStatus("offline");
        }
      } catch (err) {
        console.error(err);
        setNodeStatus("offline");
      }
      setCountdown(interval); // reset countdown after fetch
    };

    if (node_idNode && isRunning) {
      fetchDevices();
      intervalId = setInterval(fetchDevices, interval * 1000);
      setCountdown(interval);
      countdownIntervalId = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            return interval;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
      if (countdownIntervalId) clearInterval(countdownIntervalId);
    };
  }, [node_idNode, interval, isRunning]);

  if (loading) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
      <Card sx={{ height: "100%", mt: 2 }}>
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ width: "100%" }}
          >
            <Box
              sx={{
                bgcolor: nodeStatus === "online" ? "#4caf50" : "#f44336",
                color: "white",
                px: 1,
                py: 0.5,
                borderRadius: 1,
                display: "inline-block",
                fontSize: "0.875rem",
                alignSelf: { xs: "flex-start", sm: "center" },
                mr: 1,
              }}
            >
              {nodeStatus === "online"
                ? t("dashboard.onlineOffline.online")
                : t("dashboard.onlineOffline.offline")}
            </Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 0.5, sm: 2 }}
              sx={{ width: "100%" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Typography
                component="h2"
                variant="h4"
                sx={{
                  fontWeight: "600",
                  fontSize: { xs: "1.5rem", sm: "1.5rem" },
                }}
              >
                {nodeData?.name || "N/A"}
              </Typography>
              <Typography
                component="span"
                variant="subtitle1"
                sx={{
                  fontWeight: "600",
                  color: "text.secondary",
                  display: { xs: "none", sm: "inline" },
                }}
              >
                -
              </Typography>
              <Typography
                component="h2"
                variant="h6"
                sx={{
                  fontWeight: "600",
                  color: "text.secondary",
                  fontSize: { xs: "0.9rem", sm: "1.25rem" },
                }}
              >
                {nodeData?.city && nodeData?.town
                  ? `${nodeData.city}/${nodeData.town}`
                  : "N/A"}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <IntervalSelector
                sx={{
                  display: { xs: "none", md: "flex" },
                  ml: 2,
                }}
              />
              {/* Countdown CircularProgress - moved to far right */}
              {isRunning && (
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                    alignItems: "center",
                    ml: 2,
                  }}
                >
                  <CircularProgress
                    variant="determinate"
                    value={interval ? (countdown / interval) * 100 : 0}
                    size={32}
                    thickness={5}
                    sx={{ color: "#1976d2" }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      color="text.primary"
                      sx={{ fontWeight: 600, lineHeight: 1 }}
                    >
                      {countdown}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <Box
        sx={{ borderBottom: 1, borderColor: "divider", mt: 2, width: "100%" }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="scrollable tabs example"
          sx={{
            "& .MuiTab-root": {
              mr: { xs: 1, sm: 2, md: 4 },
              minHeight: { xs: "48px", sm: "64px" },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            },
            "& .MuiTabs-scrollButtons": {
              "&.Mui-disabled": { opacity: 0.3 },
            },
          }}
        >
          <Tab
            value={0}
            icon={<SummarizeIcon sx={{ color: blue[500] }} />}
            label={t("nodeComponents.summary.latestValues")}
            sx={{
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
            }}
            {...a11yProps(0)}
          />
          <Tab
            value={1}
            icon={<TelemetryIcon sx={{ color: green[500] }} />}
            label={t("nodeComponents.telemetryHistory.title")}
            sx={{
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
            }}
            {...a11yProps(1)}
          />
          <Tab
            value={2}
            icon={<ReportGmailerrorredIcon sx={{ color: brown[500] }} />}
            label={t("nodeComponents.alarmHistory.title")}
            sx={{
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
            }}
            {...a11yProps(2)}
          />
          {!isRcu && (
            <Tab
              value={3}
              icon={<LogsIcon sx={{ color: "#3d5afe" }} />}
              label={t("nodeComponents.logs.title")}
              sx={{
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
              }}
              {...a11yProps(3)}
            />
          )}
          {!isRcu && (
            <Tab
              value={4}
              icon={<CommandIcon sx={{ color: deepPurple[500] }} />}
              label={t("nodeComponents.command.title")}
              sx={{
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
              }}
              {...a11yProps(4)}
            />
          )}
          <Tab
            value={5}
            icon={<SettingsIcon sx={{ color: orange[500] }} />}
            label={t("nodeComponents.configuration.title")}
            sx={{
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
            }}
            {...a11yProps(5)}
          />
          {!isRcu && (
            <Tab
              value={6}
              icon={<AssessmentIcon sx={{ color: cyan[500] }} />}
              label={t("nodeComponents.reports.title", "Raporlar")}
              sx={{
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
              }}
              {...a11yProps(6)}
            />
          )}
          {isSarj && (
            <Tab
              value={7}
              icon={<ElectricMeterIcon sx={{ color: green[700] }} />}
              label={t("nodeComponents.consumptionSettings.title", "Tüketim Ayarları")}
              sx={{
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
              }}
              {...a11yProps(7)}
            />
          )}
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0}>
        <Summary />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <TelemetryHistory />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2}>
        <AlarmHistory />
      </CustomTabPanel>
      {!isRcu && (
        <CustomTabPanel value={value} index={3}>
          <Logs />
        </CustomTabPanel>
      )}
      {!isRcu && (
        <CustomTabPanel value={value} index={4}>
          <Command />
        </CustomTabPanel>
      )}
      <CustomTabPanel value={value} index={5}>
        <Configuration />
      </CustomTabPanel>
      {!isRcu && (
        <CustomTabPanel value={value} index={6}>
          <Reports />
        </CustomTabPanel>
      )}
      {isSarj && (
        <CustomTabPanel value={value} index={7}>
          <ConsumptionSettings />
        </CustomTabPanel>
      )}
    </Box>
  );
}
